<template>
    <DashboardSectionCard
        :title="t('vue.dashboard_overview_alerts_title')"
        :subtitle="t('vue.dashboard_overview_alerts_subtitle')"
        :link-href="settingsHref"
        :link-label="t('vue.dashboard_overview_alerts_link')"
    >
        <div
            class="mb-4 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
        >
            <span class="flex items-center gap-2 text-[13px] text-gray-600">
                <BellAlertIcon class="h-[18px] w-[18px] text-gray-500" />
                {{ t("vue.dashboard_overview_alerts_state") }}
            </span>
            <DashboardStatusBadge
                :status="alerts.enabled ? 'enabled' : 'disabled'"
                :label="stateLabel"
            />
        </div>

        <p v-if="!alerts.configured" class="text-sm text-gray-600">
            {{ t("vue.dashboard_overview_alerts_not_configured") }}
        </p>

        <template v-else>
            <div
                v-for="row in rows"
                :key="row.label"
                class="flex min-h-[34px] items-center justify-between border-t border-gray-200 text-[13px] leading-[18px]"
            >
                <span class="text-gray-600">{{ row.label }}</span>
                <span class="font-medium text-gray-900">{{ row.value }}</span>
            </div>
        </template>
    </DashboardSectionCard>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { BellAlertIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import DashboardSectionCard from "./DashboardSectionCard.vue";
import DashboardStatusBadge from "./DashboardStatusBadge.vue";
import { useDashboardRelativeTime } from "../composables/useDashboardRelativeTime";
import type { IAlertsOverview } from "@/types/Dashboard/IDashboardOverview";

interface AlertRow {
    label: string;
    value: string;
}

const props = defineProps<{
    alerts: IAlertsOverview;
}>();

const { t } = useTranslations();
const { formatRelativeTime } = useDashboardRelativeTime();
const page = usePage<IRhizomPageProps>();

const SECONDS_PER_HOUR = 3600;

const KNOWN_TIME_FRAME_KEYS: Record<number, string> = {
    86400: "vue.time_frame_24h",
    172800: "vue.time_frame_48h",
    604800: "vue.time_frame_7d",
};

const settingsHref = computed<string | null>(() =>
    page.props.hasFreePlan ? null : route("alerts.settings.index"),
);

const stateLabel = computed<string>(() => {
    if (!props.alerts.configured) {
        return t("vue.dashboard_overview_alerts_state_not_configured");
    }

    return props.alerts.enabled
        ? t("vue.dashboard_overview_state_enabled")
        : t("vue.dashboard_overview_state_disabled");
});

const resolveTimeFrame = (seconds: number | null): string => {
    if (seconds === null) {
        return "—";
    }

    const knownKey = KNOWN_TIME_FRAME_KEYS[seconds];

    return knownKey !== undefined
        ? t(knownKey)
        : t("vue.dashboard_overview_alerts_hours", {
              count: Math.round(seconds / SECONDS_PER_HOUR),
          });
};

const rows = computed<AlertRow[]>(() => [
    {
        label: t("vue.dashboard_overview_alerts_threshold"),
        value:
            props.alerts.complianceDropPercent === null
                ? "—"
                : `${props.alerts.complianceDropPercent}%`,
    },
    {
        label: t("vue.dashboard_overview_alerts_time_frame"),
        value: resolveTimeFrame(props.alerts.timeFrameSeconds),
    },
    {
        label: t("vue.dashboard_overview_alerts_last_sent"),
        value:
            props.alerts.lastSentAt === null
                ? t("vue.dashboard_overview_alerts_never_sent")
                : formatRelativeTime(props.alerts.lastSentAt),
    },
]);
</script>
